import Table from "../../Components/Table";
import useFetchData from "../../Hooks/useFetchData";
import useTableControls from "../../Hooks/useTableControls";
import { getPaymentRecords } from "../../Services/Ride";
import { format_date, getDriverDisplayName, getSerialNumber } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import { formatBookingCode } from "../../Utils/bookingCode";

const formatStatusLabel = (value) => {
  if (!value) return "N/A";
  return String(value).replace(/_/g, " ");
};

const hasPaymentHold = (item) =>
  Boolean(item?.is_paid ?? item?.isPaid) ||
  Boolean(item?.transactionId || item?.stripePaymentId);

const getPaymentStatusDisplay = (item) => {
  const rideStatus = String(item?.rideStatus || item?.status || "").toLowerCase();
  const paymentStatus = String(item?.paymentStatus || "").toLowerCase();
  const refundedAmount = Number(item?.refundedAmount);
  const isPaid = hasPaymentHold(item);
  const cancelledBy = String(item?.cancelledBy || "").toLowerCase();

  if (paymentStatus === "refunded" || refundedAmount > 0) {
    return { label: "Refunded", className: "cancelled" };
  }

  if (paymentStatus === "partially_refunded") {
    return { label: "Partial Refund", className: "pending" };
  }

  if (rideStatus === "expired") {
    return {
      label: isPaid ? "Hold released — not charged" : "Not charged",
      className: "cancelled",
    };
  }

  if (rideStatus === "cancelled") {
    if (!isPaid) {
      return { label: "Not charged", className: "pending" };
    }
    if (cancelledBy === "driver") {
      return { label: "Hold released — full void", className: "cancelled" };
    }
    return { label: "Hold released — voided", className: "cancelled" };
  }

  if (rideStatus === "completed") {
    const captured =
      ["paid", "completed", "success"].includes(paymentStatus) || isPaid;

    return {
      label: captured ? "Captured" : "Not captured",
      className: captured ? "completed" : "rejected",
    };
  }

  if (rideStatus === "pending" || !rideStatus) {
    return {
      label: isPaid ? "Authorized (hold placed)" : "Pending payment",
      className: "pending",
    };
  }

  if (["accepted", "arrived", "started", "ongoing"].includes(rideStatus)) {
    return {
      label: isPaid ? "Authorized (hold placed)" : "Payment pending",
      className: "ongoing",
    };
  }

  if (["paid", "completed", "success"].includes(paymentStatus)) {
    return { label: "Captured", className: "completed" };
  }

  return {
    label: isPaid ? "Paid" : "Pending",
    className: "pending",
  };
};

const getRefundedDisplay = (item) => {
  const refundedAmount = Number(item?.refundedAmount);

  if (Number.isFinite(refundedAmount) && refundedAmount > 0) {
    return getAmount(refundedAmount);
  }

  return "—";
};

const getRideStatusClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "completed") return "completed";
  if (["expired", "cancelled", "rejected"].includes(key)) return "cancelled";
  if (["started", "ongoing", "accepted", "arrived"].includes(key)) return "ongoing";
  return "pending";
};

const getUserName = (item) => {
  const user = item?.user || item?.bookingUser || item?.bookingData?.user;
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return name || item?.userName || "N/A";
};

const getDriverName = (item) => {
  if (item?.driverName?.trim()) return item.driverName.trim();

  const driver =
    item?.driver ||
    item?.bookingDriver ||
    item?.paidTo ||
    item?.bookingData?.driver;

  return getDriverDisplayName(driver, "Not Assigned");
};

const getBookingId = (item) => {
  if (item?.bookingCode) return item.bookingCode;
  const id = item?.bookingId || item?.booking?._id || item?.booking || item?._id;
  return formatBookingCode(id);
};

const getLocation = (item, type) => {
  if (type === "pickup") {
    return item?.pickup || item?.pickupLocation || "N/A";
  }
  return item?.dropoff || item?.dropoffLocation || "N/A";
};

const getAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "N/A";
};

const getDriverShare = (item) => {
  if (item?.driverShare != null) return getAmount(item.driverShare);

  const total = Number(item?.totalAmount ?? item?.amount ?? item?.bookingDetails?.price);
  const admin = Number(item?.adminShare ?? item?.bookingDetails?.adminEarnedAmount);
  if (Number.isFinite(total) && Number.isFinite(admin)) {
    return getAmount(Math.max(total - admin, 0));
  }
  return "N/A";
};

const getAdminShare = (item) => {
  if (item?.adminShare != null) return getAmount(item.adminShare);
  if (item?.bookingDetails?.adminEarnedAmount != null) {
    return getAmount(item.bookingDetails.adminEarnedAmount);
  }
  return "N/A";
};

const getTotalAmount = (item) => {
  const total =
    item?.totalAmount ??
    item?.amount ??
    item?.bookingDetails?.price ??
    item?.bookingData?.bookingDetails?.price;

  return total != null ? getAmount(total) : "N/A";
};

const getPaymentRecordsMeta = (response) => {
  const body = response?.data || {};
  const records = Array.isArray(body?.data)
    ? body.data
    : body?.data?.docs || body?.data?.records || [];
  const pagination = body?.pagination || body?.data?.pagination || {};

  return {
    records,
    page: pagination.page || 1,
    limit: pagination.limit || records.length || 10,
    totalPages: pagination.totalPages || 0,
    totalDocs: pagination.total ?? pagination.totalDocs ?? records.length,
  };
};

export default function PaymentLogs() {
  const {
    perPage,
    setPerPage,
    search_string,
    setSearchString,
    from,
    setFrom,
    to,
    setTo,
  } = useTableControls();

  const {
    page,
    setPage,
    isFetching,
    isLoading,
    data,
  } = useFetchData("payment-records", getPaymentRecords, [
    perPage,
    search_string,
    from,
    to,
  ]);

  const { records: logs, page: currentPage, limit, totalPages, totalDocs } =
    getPaymentRecordsMeta(data);

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="row">
              <div className="col-12">
                <div className="user-details-wrapper">
                  <PageHeader
                    title="Payment Logs"
                    subtitle="Monitor transaction history, platform fees, and payment reconciliations"
                  />
                  <Table
                    key="payment-logs-table"
                    lastColumnIsAction={false}
                    tableTitles={[
                      "S.No.",
                      "Booking Id",
                      "User Name",
                      "Driver Name",
                      "Pickup Location",
                      "Drop-off Location",
                      "Total Amount",
                      "Driver Share",
                      "Admin Share",
                      "Payment Status",
                      "Ride Status",
                      "Refunded",
                      "Paid At",
                      "Transaction ID",
                    ]}
                    dateFilter
                    showEntries
                    isLoading={isLoading}
                    isFetching={isFetching}
                    data={logs}
                    totalPages={Math.ceil(totalPages || 0)}
                    totalDocs={totalDocs}
                    page={page}
                    setPage={setPage}
                    setSearchString={setSearchString}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    from={from}
                    setFrom={setFrom}
                    to={to}
                    setTo={setTo}
                  >
                    {logs.map((item, index) => {
                      const transactionId =
                        item?.transactionId || item?.stripePaymentId || "";
                      const paymentStatus = getPaymentStatusDisplay(item);

                      return (
                      <tr key={item?._id || item?.bookingId || index}>
                        <td>{getSerialNumber({ page: currentPage, limit }, index)}</td>
                        <td>{getBookingId(item)}</td>
                        <td>{getUserName(item)}</td>
                        <td>{getDriverName(item)}</td>
                        <td>{getLocation(item, "pickup")}</td>
                        <td>{getLocation(item, "dropoff")}</td>
                        <td>{getTotalAmount(item)}</td>
                        <td>{getDriverShare(item)}</td>
                        <td>{getAdminShare(item)}</td>
                        <td className="text-capitalize">
                          <span className={`table-status ${paymentStatus.className}`}>
                            {paymentStatus.label}
                          </span>
                        </td>
                        <td className="text-capitalize">
                          <span
                            className={`table-status ${getRideStatusClass(item?.rideStatus)}`}
                          >
                            {formatStatusLabel(item?.rideStatus)}
                          </span>
                        </td>
                        <td>{getRefundedDisplay(item)}</td>
                        <td>
                          {item?.paidAt
                            ? format_date(item.paidAt, "MM/DD/YYYY")
                            : "N/A"}
                        </td>
                        <td
                          className="payment-transaction-id"
                          title={transactionId || undefined}
                        >
                          {transactionId || "N/A"}
                        </td>
                      </tr>
                      );
                    })}
                  </Table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <style>{`
        .fw-800 { font-weight: 800; }
        .w-100-mobile { width: 100% !important; text-align: center; justify-content: center; }

        @media (max-width: 576px) {
          .w-100-mobile { width: 100% !important; text-align: center; justify-content: center; }
          .user-details-wrapper { padding: 0.5rem; }
        }
      `}</style>
    </div>
  );
}
